import { USER_ID_KEY } from '~/constants';
import {
  AnalyticsEventNames,
  PurchaseEventParameters,
} from '~/services/analytics/types';
import AbstractStorage from '~/services/storage/AbstractStorage';

const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

export interface AnalyticsItem {
  init: (userId: string | null) => void;
  trackEvent: (name: string, parameters?: Record<string, any>) => void;
  trackPurchase: (name: string, parameters: PurchaseEventParameters) => void;
  trackRegistration?: () => void;
  setUserProperties?: (properties: Record<string, any>) => void;
  setUserPropertiesOnce?: (properties: Record<string, any>) => void;
  getDeviceId?: () => string | null;
}

class AnalyticManager {
  subscribers: AnalyticsItem[];
  userId: string | null;
  defaultEventParams: Record<string, any>;
  initialUserProperties: Record<string, any>;
  isInitialized: boolean;
  storage: AbstractStorage;
  debouncedEventName: string | null;
  debounceTimer: null | number;

  constructor(subscribers: AnalyticsItem[], storage: AbstractStorage) {
    this.isInitialized = false;
    this.storage = storage;
    this.subscribers = subscribers || [];
    this.userId = null;
    this.defaultEventParams = {};
  }

  init(data: {
    userId: string | null;
    eventParams: Record<string, any>;
    userProperties: Record<string, any>;
  }): void {
    this.userId = data.userId;
    this.defaultEventParams = data.eventParams;
    this.initialUserProperties = data.userProperties;
    this.subscribers.forEach((subscriber) => {
      subscriber.init(this.userId);
      if (subscriber && subscriber.setUserProperties) {
        subscriber.setUserProperties(data.userProperties);
      }
    });

    this.storage.setByKey(USER_ID_KEY, this.userId);
    this.isInitialized = true;
    if (IS_DEVELOPMENT) {
      console.warn('update analytics user id: ', this.userId);
    }
  }

  addSubscriber(analyticItem: AnalyticsItem): void {
    this.subscribers.push(analyticItem);
  }

  checkAndUpdateUserId(newUserId: string | null): void {
    if (!this.isInitialized) return;
    const prevUserId = this.storage.getByKey(USER_ID_KEY);

    if (newUserId !== prevUserId) {
      this.init({
        userId: newUserId,
        eventParams: this.defaultEventParams,
        userProperties: this.initialUserProperties,
      });
    }
  }

  addDefaultEventParams(params: Record<string, string>): void {
    if (!this.isInitialized) return;

    this.defaultEventParams = { ...this.defaultEventParams, ...params };
  }

  setUserProperties(properties: Record<string, any>): void {
    if (!this.isInitialized) return;

    this.subscribers.forEach((subscriber) => {
      subscriber.setUserProperties && subscriber.setUserProperties(properties);
    });
  }

  setUserPropertiesOnce(properties: Record<string, any>): void {
    if (!this.isInitialized) return;

    this.subscribers.forEach((subscriber) => {
      subscriber.setUserPropertiesOnce &&
        subscriber.setUserPropertiesOnce(properties);
    });
  }

  trackEvent(
    name: AnalyticsEventNames,
    parameters?: Record<string, any>,
    skipDebounce = false,
  ): void {
    if (!this.isInitialized) {
      return;
    }

    if (!skipDebounce && this.debouncedEventName === name) {
      return;
    }

    this.debouncedEventName = name;

    this.debounceTimer && clearTimeout(this.debounceTimer);

    this.debounceTimer = window.setTimeout(() => {
      this.debouncedEventName = null;
    }, 1000);

    const extraParams = { ...this.defaultEventParams, ...parameters };

    this.subscribers.forEach((subscriber) => {
      subscriber.trackEvent(name, extraParams);
    });
  }

  trackRegistration(): void {
    this.subscribers.forEach((subscriber) => {
      subscriber.trackRegistration?.();
    });
  }

  trackPurchase(
    name: AnalyticsEventNames,
    parameters: PurchaseEventParameters,
  ): void {
    const extraParams = { ...this.defaultEventParams, ...parameters };

    if (IS_DEVELOPMENT) {
      console.warn('track event name: ', name, extraParams);
    }

    this.subscribers.forEach((subscriber) => {
      subscriber.trackPurchase(name, extraParams);
    });
  }

  getDeviceId(): string | null {
    for (const subscriber of this.subscribers) {
      const deviceId = subscriber.getDeviceId?.();
      if (deviceId) {
        return deviceId;
      }
    }
    return null;
  }
}

export default AnalyticManager;
